import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  useSchedulerShiftsCalendarDataQuery,
  useSchedulersWithTargetsQuery,
  SchedulerShiftsCalendarDataQuery,
  SchedulerShiftsCalendarDataDocument,
} from '../../../__generated__/graphql';
import {
  BryntumCalendar,
  BryntumCalendarProps,
} from '@bryntum/calendar-react-thin';
import { Calendar, CalendarView } from '@bryntum/calendar-thin';
import { EventModel, EventModelConfig } from '@bryntum/scheduler-thin';
import { CalendarViewMode, ShiftBreak } from './types';
import { getDateRangeForMode } from './utils';
import { useUserInfo } from '../../../context/UserInfo';
import ServerError from '../../../../../components/ServerError';
import CalendarLoading from './CalendarLoading';
import './styles.css';
import { EventContent } from './EventContent';
import { OpenShiftEventContent } from './OpenShiftEventContent';
import { ShiftSidePanel } from './ShiftSidePanel';
import { ShiftLabel } from './types';
import { useCurrentLocale } from '../../../../../context/I18n';
import { useNamespacedTranslation } from '../../../../../hooks/useNamespacedTranslation';
import ModalSidebar, {
  ModalSidebarContent,
} from '../../../components/ModalSidebar';
import { mapLocaleToBryntumLocale } from '../../../../../utils/bryntumCalendarLocale';
import { OpenShiftSidePanel } from '../components/OpenShiftSidePanel';
import { useOpenShiftActions } from '../components/useOpenShiftActions';
import { mapOpenShiftsToEvents } from '../components/mapOpenShiftsToEvents';
import { Form } from 'react-bootstrap';
import { parseISO } from 'date-fns';
import type {
  OpenShiftState,
  OpenShiftNotAvailableReason,
} from '../../../components/Shifts/Common/types';
import type { SelectedOpenShift } from '../components/openShiftConstants';
import { useFlairLocalStorage } from '../../../hooks/useFlairLocalStorage';
import { useSchedulerShiftsUrlSync } from './useSchedulerShiftsUrlSync';

type EventModalData = EventModel & {
  targetRecordName: string;
  shiftLabels: ShiftLabel[];
  breakPeriodMinutes?: number;
  breaks?: ShiftBreak[];
  employeeNotes?: string;
  hasPendingChangeRequests?: boolean;
};

type ShiftsCalendarProps = {
  currentDay: Date;
  mode: CalendarViewMode;
  onDateChange: (date: Date) => void;
  onModeChange: (mode: CalendarViewMode) => void;
  data: SchedulerShiftsCalendarDataQuery;
  targetNameMap: Record<string, string>;
  showOpenShifts: boolean;
  onShowOpenShiftsChange: (value: boolean) => void;
};

const ShiftsCalendarView: React.FC<ShiftsCalendarProps> = ({
  currentDay,
  mode,
  onDateChange,
  onModeChange,
  data,
  targetNameMap,
  showOpenShifts,
  onShowOpenShiftsChange,
}) => {
  const locale = useCurrentLocale();
  const t = useNamespacedTranslation('schedulerShifts');
  const calendarRef = useRef<BryntumCalendar>(null);
  const [selectedEvent, setSelectedEvent] = useState<EventModalData | null>(
    null,
  );
  const [selectedOpenShift, setSelectedOpenShift] =
    useState<SelectedOpenShift | null>(null);

  const closeOpenShiftSidebar = useCallback(() => {
    setSelectedOpenShift(null);
  }, []);

  const { requestShift, cancelRequest, pendingAction } = useOpenShiftActions(
    SchedulerShiftsCalendarDataDocument,
    closeOpenShiftSidebar,
  );

  const meId = data?.me?.Id ?? '';

  const myShiftIntervals = useMemo(() => {
    if (!data?.schedulerShifts) return [];
    return data.schedulerShifts.map((s) => ({
      start: parseISO(s.flair__Start_Datetime__c),
      end: parseISO(s.flair__End_Datetime__c),
    }));
  }, [data?.schedulerShifts]);

  const openShiftLabel = t('openShift.label');

  const { regularEvents, openShiftEvents } = useMemo(() => {
    if (!data?.schedulerShifts)
      return {
        regularEvents: [] as EventModelConfig[],
        openShiftEvents: [] as EventModelConfig[],
      };

    const regular: EventModelConfig[] = data.schedulerShifts.map((shift) => ({
      id: shift.Id,
      name: shift.flair__Title__c || '',
      startDate: new Date(shift.flair__Start_Datetime__c),
      endDate: new Date(shift.flair__End_Datetime__c),
      resourceId: shift.flair__Linked_Resource_Id__c,
      eventColor: shift.flair__Shift_Color__c || undefined,
      targetRecordName: targetNameMap[shift.flair__Linked_Target_Id__c] ?? '',
      breakPeriodMinutes: shift.flair__Total_Break_Period_in_Minutes__c || 0,
      breaks:
        shift.breaks?.map((breakItem) => ({
          id: breakItem.Id,
          startDate: new Date(breakItem.flair__Start_Datetime__c),
          endDate: new Date(breakItem.flair__End_Datetime__c),
        })) || [],
      employeeNotes: shift.flair__Employee_Notes__c || '',
      hasPendingChangeRequests:
        shift.flair__Pending_Change_Requests_Count__c &&
        shift.flair__Pending_Change_Requests_Count__c > 0,
      shiftLabels:
        shift.shiftLabels?.map((label) => ({
          id: label.Id,
          name: label.Name,
          tagName: label.flair__Tag_Name__c,
        })) || [],
    }));

    const open: EventModelConfig[] = mapOpenShiftsToEvents({
      openShifts: data.me?.schedulerOpenShifts ?? [],
      meId,
      myShiftIntervals,
      targetNameMap,
      openShiftLabel,
    }).map((event) => ({
      ...event,
      cls: `open-shift-calendar-event open-shift--${event.openShiftState}`,
      openShiftStartDate: (event.startDate as Date).toISOString(),
      openShiftEndDate: (event.endDate as Date).toISOString(),
    }));

    return { regularEvents: regular, openShiftEvents: open };
  }, [data, meId, myShiftIntervals, targetNameMap, openShiftLabel]);

  const handleDateChange = (event: {
    source: Calendar;
    oldDate: Date;
    date: Date;
  }) => {
    onDateChange(event.date);
  };

  const handleActiveItemChange = ({
    source,
  }: {
    source: Calendar;
    prevActiveItem: CalendarView;
    activeItem: CalendarView;
    containedFocus: boolean;
  }) => {
    const newMode = source.mode;
    if (newMode && newMode !== mode) {
      onModeChange(newMode);
    }
  };

  useEffect(() => {
    const calendar = calendarRef.current?.instance;
    if (calendar) {
      const bryntumLocaleName = mapLocaleToBryntumLocale(locale);
      calendar.localeManager.locale = bryntumLocaleName;
    }
  }, [locale]);

  useEffect(() => {
    const calendar = calendarRef.current?.instance;
    if (calendar) {
      calendar.eventStore.data = showOpenShifts
        ? [...regularEvents, ...openShiftEvents]
        : regularEvents;
    }
  }, [regularEvents, openShiftEvents, showOpenShifts]);

  const customEventRenderer = (detail: { eventRecord: EventModel }) => {
    const { eventRecord } = detail;

    if (eventRecord.getData('isOpenShift')) {
      return (
        <OpenShiftEventContent
          name={eventRecord.name || ''}
          targetName={eventRecord.getData('targetName') as string | null}
          state={eventRecord.getData('openShiftState') as OpenShiftState}
          breakPeriodMinutes={
            (eventRecord.getData('breakPeriodMinutes') as number) || 0
          }
        />
      );
    }

    const event = eventRecord as EventModalData;
    return (
      <EventContent
        name={event.name || ''}
        targetRecordName={event.targetRecordName || ''}
        shiftLabels={event?.shiftLabels}
        breakPeriodMinutes={event.breakPeriodMinutes}
        employeeNotes={event.employeeNotes}
        hasPendingChangeRequests={event.hasPendingChangeRequests || false}
      />
    );
  };

  const handleEventClick = (event: { eventRecord: EventModel }) => {
    const { eventRecord } = event;

    if (eventRecord.getData('isOpenShift')) {
      setSelectedEvent(null);
      setSelectedOpenShift({
        id: eventRecord.id.toString(),
        name: eventRecord.name || '',
        startDate: eventRecord.getData('openShiftStartDate') as string,
        endDate: eventRecord.getData('openShiftEndDate') as string,
        targetName: eventRecord.getData('targetName') as string | null,
        breakPeriodMinutes:
          (eventRecord.getData('breakPeriodMinutes') as number) || 0,
        state: eventRecord.getData('openShiftState') as OpenShiftState,
        notAvailableReason: eventRecord.getData(
          'openShiftNotAvailableReason',
        ) as OpenShiftNotAvailableReason | undefined,
        openShiftId: eventRecord.getData('openShiftId') as string,
        assignmentRequestId: eventRecord.getData('assignmentRequestId') as
          | string
          | null,
      });
      return;
    }

    setSelectedOpenShift(null);
    setSelectedEvent(eventRecord as EventModalData);
  };

  const handleCloseSidebar = () => {
    setSelectedEvent(null);
  };

  const calendarConfig: BryntumCalendarProps = {
    date: currentDay,
    mode,
    onDateChange: handleDateChange,
    onActiveItemChange: handleActiveItemChange,
    onEventClick: handleEventClick,
    readOnly: true,
    eventEditFeature: false,
    eventTooltipFeature: false,
    modes: {
      day: {
        showAllDayHeader: false,
        eventRenderer: customEventRenderer,
      },
      week: {
        showAllDayHeader: false,
        eventRenderer: customEventRenderer,
      },
    },
  };

  return (
    <>
      {openShiftEvents.length > 0 && (
        <div className="d-flex align-items-center px-3 py-3 bg-white shadow-sm">
          <Form.Check
            type="checkbox"
            id="show-open-shifts-calendar"
            label={t('showOpenShifts', { count: openShiftEvents.length })}
            checked={showOpenShifts}
            onChange={(e) => onShowOpenShiftsChange(e.target.checked)}
            className="mb-0"
          />
        </div>
      )}
      <div className="d-flex flex-column calendar-height">
        <BryntumCalendar ref={calendarRef} {...calendarConfig} />
      </div>
      <ModalSidebar
        show={!!selectedEvent}
        onClose={handleCloseSidebar}
        header={selectedEvent?.name || ''}
        content={
          selectedEvent && (
            <ModalSidebarContent
              body={
                <ShiftSidePanel
                  shiftId={selectedEvent.id.toString()}
                  targetName={selectedEvent.targetRecordName}
                />
              }
            />
          )
        }
      />
      <ModalSidebar
        show={!!selectedOpenShift}
        onClose={closeOpenShiftSidebar}
        header={selectedOpenShift?.name || ''}
        content={
          selectedOpenShift && (
            <ModalSidebarContent
              body={
                <OpenShiftSidePanel
                  name={selectedOpenShift.name}
                  startDate={selectedOpenShift.startDate}
                  endDate={selectedOpenShift.endDate}
                  targetName={selectedOpenShift.targetName}
                  breakPeriodMinutes={selectedOpenShift.breakPeriodMinutes}
                  state={selectedOpenShift.state}
                  notAvailableReason={selectedOpenShift.notAvailableReason}
                  onRequest={() => requestShift(selectedOpenShift.openShiftId)}
                  onCancelRequest={() => {
                    if (selectedOpenShift.assignmentRequestId) {
                      cancelRequest(
                        selectedOpenShift.openShiftId,
                        selectedOpenShift.assignmentRequestId,
                      );
                    }
                  }}
                  isPending={
                    pendingAction?.openShiftId === selectedOpenShift.openShiftId
                  }
                />
              }
            />
          )
        }
      />
    </>
  );
};

export const SchedulerShiftsCalendar: React.FC = () => {
  const authUserInfo = useUserInfo();
  const { currentDay, mode, onDateChange, onModeChange } =
    useSchedulerShiftsUrlSync();
  const [showOpenShifts, setShowOpenShifts] = useFlairLocalStorage(
    'my_calendar_show_open_shifts',
    true,
  );

  const { startDate, endDate } = getDateRangeForMode(currentDay, mode);

  const { data, loading, error } = useSchedulerShiftsCalendarDataQuery({
    variables: {
      filter: { startDate, endDate, employeesIds: [authUserInfo.id] },
      openShiftsStartDate: startDate,
      openShiftsEndDate: endDate,
    },
  });

  const { data: targetsData } = useSchedulersWithTargetsQuery();

  const targetNameMap = useMemo(() => {
    const map: Record<string, string> = {};
    for (const s of targetsData?.me?.schedulersWithTargets ?? []) {
      for (const t of s.allTargets) {
        if (!map[t.id]) map[t.id] = t.name;
      }
    }
    return map;
  }, [targetsData?.me?.schedulersWithTargets]);

  if (loading) {
    return <CalendarLoading currentMode={mode} />;
  }

  if (!data || error) {
    return <ServerError />;
  }

  return (
    <ShiftsCalendarView
      currentDay={currentDay}
      mode={mode}
      onDateChange={onDateChange}
      onModeChange={onModeChange}
      data={data}
      targetNameMap={targetNameMap}
      showOpenShifts={showOpenShifts}
      onShowOpenShiftsChange={setShowOpenShifts}
    />
  );
};
